<script lang="ts" setup>
import ChatMessageItem from "@/components/mobile/ChatMessageItem.vue";
import { useChatMessageList } from "@/composable/chat/useChatMessageList";
import { DEFAULT_COST, ModelList } from "@/enums/chatEnums";
import { useChatSession } from "@/stores/chatSession";
import { useMate } from "@/stores/mate";
import {
  ChatMessageItemInstance,
  IMessageItem,
  MessageListInstance,
} from "@/types/component";
import { Mate } from "@/types/entity";
import { marked } from "marked";
import { List } from "vant";
import { computed, nextTick, ref } from "vue";
import { isLockMsg, replacePlaceHolder } from "../mode/util";
import { BubbleActionPopupInstance } from "../popup/BubbleActionPopup.vue";

const props = withDefaults(
  defineProps<{
    sessionId: number;
    mate?: Mate;
    myMessage: string;
    streamMsg: string;
    isPostingMsg: "start" | "end" | false;
    isStreamReplying: boolean;
    enableTool?: boolean;
    enableReactive?: boolean;
  }>(),
  {
    enableTool: false,
    enableReactive: true,
  }
);

const emit = defineEmits<{
  (e: "on-continue-btn-click", targets: string[]): void;
}>();

const mateStore = useMate();
const sessionStore = useChatSession();
const currentSession = computed(() => sessionStore.sessionMap[props.sessionId]);
// Element Ref
const messageContainerRef = ref<HTMLElement | null>(null);
const scrollRef = ref<HTMLDivElement | null>(null);
const bubbleActionRef = ref<BubbleActionPopupInstance | null>(null);
const chatMessageRefs = ref<ChatMessageItemInstance[]>([]);

const continueActionTargets = ref<string[]>([]);

const {
  messageList,
  scrollLoading,
  finished,
  error,
  scrollLoad,
  scrollToBottom,
} = useChatMessageList({
  sessionId: props.sessionId,
  messageContainerRef,
  scrollRef,
  myMessage: computed(() => props.myMessage),
  streamMsg: computed(() => props.streamMsg),
  isStreamReplying: computed(() => props.isStreamReplying),
});

const scenario = computed(() => {
  const scenarioText =
    currentSession.value?.chatScene?.scenario || props.mate?.scenario;
  if (!scenarioText || !props.mate?.name || !mateStore.currentMate?.name) {
    return "";
  }
  return marked.parse(
    replacePlaceHolder(
      scenarioText,
      props.mate.name,
      mateStore.currentMate?.name
    )
  );
});
const isPrivate = computed(() => {
  if (!currentSession.value) {
    return false;
  }
  return currentSession.value?.isPrivate === 1;
});
const unlockCost = computed(() =>
  Number(
    ModelList.find(
      (item) =>
        item.value === currentSession.value?.model &&
        item.costType === "crystal"
    )?.cost ?? DEFAULT_COST
  )
);
const showContinueBtn = (item: IMessageItem) => {
  return (
    !props.isPostingMsg &&
    !props.isStreamReplying &&
    item.id === messageList.value[messageList.value.length - 1].id &&
    (!item.requireUnlock || item.isPaid)
  );
};
const onContinueBtnClick = () => {
  emit("on-continue-btn-click", continueActionTargets.value);
};
const playAudioMsg = (id: number) => {
  nextTick(() => {
    const target = chatMessageRefs.value.find((item) => item.id === id);
    if (target) {
      target.audioRef?.playAudio();
    }
  });
};

defineExpose<MessageListInstance>({
  scrollToBottom: scrollToBottom,
  playAudioMsg: playAudioMsg,
});
</script>

<template>
  <div ref="scrollRef" class="h-full overflow-y-scroll">
    <div v-if="!mate || !currentSession"></div>
    <list
      v-else
      v-model:error="error"
      v-model:loading="scrollLoading"
      :error-text="$t('Message.requestFailedClickToReload')"
      :finished="finished"
      :finished-text="$t('Message.noMoreAvailable')"
      :immediate-check="false"
      direction="up"
      offset="0"
      @load="scrollLoad"
    >
      <div
        v-if="scenario && isPrivate"
        class="flex items-center justify-center p-4 border-b 0"
      >
        <div class="text-base-content/50">
          <span class="font-semibold"
            >{{ $t("User.mate.module.chatScene") }}: </span
          ><span class="chat-container" v-html="scenario"></span>
        </div>
      </div>
      <div ref="messageContainerRef" class="p-4">
        <chat-message-item
          v-for="(item, idx) in messageList"
          :id="item.id"
          :key="item.id"
          ref="chatMessageRefs"
          :audio-url="item.audioUrl"
          :avatar-url="item.mediaUrl"
          :current-mate-tone-id="currentSession?.mateToneId"
          :enable-audio="!!item.msg && !item.isCurrentUser"
          :enable-avatar-url="true"
          :enable-reactive="enableReactive && isPrivate"
          :is-locked="isLockMsg(item)"
          :mate-uuid="item.mateUuid"
          :mateToneId="item.mateToneId || currentSession?.mateToneId"
          :media="item.media"
          :msg="item.msg"
          :position="item.isCurrentUser ? 'end' : 'start'"
          :require-unlock="item.requireUnlock"
          :time="!isPrivate ? item.time : undefined"
          :unlock-cost="unlockCost"
          enable-avatar-click
          @on-long-press="
            bubbleActionRef?.showBubbleActions(
              item,
              sessionId,
              isPrivate,
              idx === messageList.length - 1 && !item.isCurrentUser
            )
          "
          @on-edit="bubbleActionRef?.editMsg(item, sessionId)"
        >
          <template
            v-if="mate && showContinueBtn(item) && enableTool"
            #extraAction
          >
            <ChatActionTargetSelector
              v-if="mate"
              :mate="mate"
              :mate-session="currentSession!"
              v-model:action-targets="continueActionTargets"
            >
              <button class="btn btn-xs btn-ghost rounded text-base-content/50">
                <span>{{ $t("Chats.continue") }}</span>
                <i class="iconify mingcute--down-small-line shrink-0" />
              </button>
              <template #header>
                <div
                  class="flex justify-between items-center gap-2 border-b pb-1"
                >
                  <span class="text-sm text-base-content/50">{{
                    $t("Chats.chooseReplier")
                  }}</span>
                  <button
                    class="btn btn-xs btn-primary rounded"
                    @click="onContinueBtnClick"
                  >
                    {{ $t("Buttons.confirm") }}
                  </button>
                </div>
              </template>
            </ChatActionTargetSelector>
          </template>
          <template #msg-start v-if="item.fromNames && !item.isCurrentUser">
            <span class="badge badge-sm absolute -top-1.5 left-0">
              {{ item.fromNames }}
            </span>
          </template>
        </chat-message-item>
        <div>
          <chat-message-item
            v-show="myMessage"
            :avatar-url="mateStore.currentMate!.mediaUrl"
            :enable-avatar-url="false"
            :mate-uuid="mateStore.currentMate!.uuid"
            :msg="myMessage"
            :position="'end'"
            enable-avatar-click
            :enable-audio="false"
          />
          <!-- StreamMsg返回中 -->
          <chat-message-item
            v-show="streamMsg"
            :avatar-url="isPostingMsg === 'start' ? mate!.mediaUrl : mateStore.currentMate?.mediaUrl"
            :enable-avatar-url="false"
            :mate-uuid="mate!.uuid"
            :msg="streamMsg"
            :position="isPostingMsg === 'start' ? 'start' : 'end'"
            enable-avatar-click
            enable-parse-md
            :enable-audio="false"
          />
          <!-- StreamMsg返回前的loading -->
          <chat-message-item
            v-show="!streamMsg && isPostingMsg"
            :avatar-url="isPostingMsg === 'start' ? mate!.mediaUrl : mateStore.currentMate?.mediaUrl"
            :enable-avatar-url="false"
            :mate-uuid="mate!.uuid"
            :position="isPostingMsg === 'start' ? 'start' : 'end'"
            enable-avatar-click
            :enable-audio="false"
          >
            <div class="flex items-center">
              <span class="loading loading-dots loading-sm text-base-content" />
            </div>
          </chat-message-item>
        </div>
      </div>
    </list>
    <BubbleActionPopup ref="bubbleActionRef" />
  </div>
</template>
